import { ChevronDown, Copy, Trash2 } from 'lucide-react';
import { Collapsible } from 'radix-ui';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { FEATURE_FLAG_KEYS, flagToUi, ProjectFeatureFlags } from '../api/types';
import { getPromptTemplateById } from '../model/promptTemplates';
import FeatureToggleGroup from './FeatureToggleGroup';
import PromptTypeSelect from './PromptTypeSelect';

export interface SubProjectDraft extends ProjectFeatureFlags {
  uid: string;
  name: string;
  promptTypeId: string;
  prompt: string;
}

interface Props {
  index: number;
  draft: SubProjectDraft;
  onChange: (next: SubProjectDraft) => void;
  onDuplicate?: () => void;
  onRemove?: () => void;
  defaultExpanded?: boolean;
}

const SubProjectCard = ({ index, draft, onChange, onDuplicate, onRemove, defaultExpanded }: Props) => {
  const enabledFeaturesCount = FEATURE_FLAG_KEYS.filter((key) => flagToUi(key, draft[key])).length;

  const handleField = <K extends keyof SubProjectDraft>(key: K, value: SubProjectDraft[K]) => {
    onChange({ ...draft, [key]: value });
  };

  const handleFeatures = (next: ProjectFeatureFlags) => {
    onChange({ ...draft, ...next });
  };

  const handlePromptType = (id: string) => {
    const template = getPromptTemplateById(id);
    onChange({
      ...draft,
      promptTypeId: id,
      prompt: template?.prompt ?? draft.prompt,
    });
  };

  return (
    <Collapsible.Root defaultOpen={defaultExpanded} className="group/sub rounded-md border border-border">
      <div className="flex items-center gap-3 p-3">
        <Collapsible.Trigger asChild>
          <button type="button" className="flex flex-1 items-center gap-3 text-left">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
              {index + 1}
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium">{draft.name || `Proyecto ${index + 1}`}</p>
              <p className="text-xs text-text-muted">
                {draft.promptTypeId || 'sin tipo'} ·{enabledFeaturesCount} features activadas
              </p>
            </div>
            <ChevronDown
              className={cn('size-4 text-text-muted transition-transform', 'group-data-[state=open]/sub:rotate-180')}
            />
          </button>
        </Collapsible.Trigger>
        {onDuplicate && (
          <Button type="button" variant="ghost" size="icon" onClick={onDuplicate} aria-label="Duplicar">
            <Copy className="size-4" />
          </Button>
        )}
        {onRemove && (
          <Button type="button" variant="ghost" size="icon" onClick={onRemove} aria-label="Eliminar">
            <Trash2 className="size-4 text-destructive" />
          </Button>
        )}
      </div>
      <Collapsible.Content>
        <div className="space-y-4 border-t border-border p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={`${draft.uid}-name`}>Nombre del proyecto</Label>
              <Input
                id={`${draft.uid}-name`}
                value={draft.name}
                onChange={(e) => handleField('name', e.target.value)}
                placeholder="Ej. Edificio Aurora"
              />
            </div>
            <PromptTypeSelect id={`${draft.uid}-prompt-type`} value={draft.promptTypeId} onChange={handlePromptType} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${draft.uid}-prompt`}>Prompt</Label>
            <Textarea
              id={`${draft.uid}-prompt`}
              value={draft.prompt}
              onChange={(e) => handleField('prompt', e.target.value)}
              rows={4}
            />
            <p className="text-xs text-text-muted">Se completa al elegir un tipo; puedes editarlo manualmente.</p>
          </div>
          <FeatureToggleGroup
            value={{
              skipFinancialDataValidationOnIncome: draft.skipFinancialDataValidationOnIncome,
              hasProperties: draft.hasProperties,
              hasSaveEmployementDetailsFunctionality: draft.hasSaveEmployementDetailsFunctionality,
              includeSaveBuyerTypeFunction: draft.includeSaveBuyerTypeFunction,
              enableRegistroCivilFeature: draft.enableRegistroCivilFeature,
              enableCamaraComercioFeature: draft.enableCamaraComercioFeature,
              documentAnalyzerEnabled: draft.documentAnalyzerEnabled,
            }}
            onChange={handleFeatures}
          />
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

export default SubProjectCard;
